import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { IoService } from '../../../services/io/io.service';
import { SupportService } from '../../../services/support/support.service';
import { SettingsService } from '../../../services/settings/settings.service';
import { LoadingComponent } from '../../shared/loading/loading.component';
import { AppComponent } from '../../../app.component';
import { DialogOption } from '../../shared/ui/dialog/dialog-options';

@Component({
  selector: 'app-view-editor',
  templateUrl: './view-editor.component.html',
  styleUrls: ['./view-editor.component.scss']
})
export class ViewEditorComponent implements OnInit {

  viewId: string;
  isNew = false;
  view = null;
  saving = false;

  supportActive = false;
  settingsActive = false;

  constructor(
    private ioService: IoService,
    private supportService: SupportService,
    private settingsService: SettingsService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.supportService.supportActive$.subscribe((active) => {
      this.supportActive = active;
    });
    this.supportActive = this.supportService.isSupportActive();

    this.settingsService.settingsActive$.subscribe((active) => {
      this.settingsActive = active;
    });
    this.settingsActive = this.settingsService.isSettingsActive();

    this.route.params.subscribe(params => {
      this.viewId = params.documentId;
      this.isNew = this.viewId === 'new';

      if (this.isNew) {
        this.setView({
          name: '',
          key: '',
          active: true,
          collection: '',
          fields: [],
          activeFields: [],
          filters: {}
        });
      } else {
        this.loadView();
      }
    });
  }

  loadView() {
    LoadingComponent.showLoading();
    this.ioService.post('/viewEditor/getView', {
      viewId: this.viewId
    }).then((view: any) => {
      this.setView(view);
      LoadingComponent.hideLoading();
    }).catch((err: any) => {
      LoadingComponent.hideLoading();
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to load view.', 'OK', 5000);
      this.router.navigate(['/editor/views']);
    });
  }

  setView(view) {
    view.fields = view.fields || [];
    view.activeFields = view.activeFields || [];
    view.filters = view.filters || {};
    view.actions = view.actions || [];
    view.importMatchFields = view.importMatchFields || [];

    this.view = view;
  }

  // ----- Fields -----

  addField() {
    this.view.fields.push({
      path: '',
      name: '',
      type: 'string'
    });
  }

  removeField(index: number) {
    const removed = this.view.fields.splice(index, 1)[0];
    if (removed && removed.path) {
      this.view.activeFields = this.view.activeFields.filter(path => path !== removed.path);
      this.view.importMatchFields = this.view.importMatchFields.filter(path => path !== removed.path);
    }
  }

  moveField(index: number, offset: number) {
    const target = index + offset;
    if (target < 0 || target >= this.view.fields.length) {
      return;
    }
    const field = this.view.fields.splice(index, 1)[0];
    this.view.fields.splice(target, 0, field);
  }

  // ----- Active Fields -----

  isActiveField(path: string): boolean {
    return this.view.activeFields.indexOf(path) !== -1;
  }

  toggleActiveField(path: string, checked: boolean) {
    if (checked && !this.isActiveField(path)) {
      this.view.activeFields.push(path);
    } else if (!checked) {
      this.view.activeFields = this.view.activeFields.filter(activePath => activePath !== path);
    }
  }

  get orphanedActiveFields() {
    const fieldPaths = this.view.fields.map(field => field.path);
    return this.view.activeFields.filter(path => fieldPaths.indexOf(path) === -1);
  }

  // ----- Import Match Fields -----

  isImportMatchField(path: string): boolean {
    return this.view.importMatchFields.indexOf(path) !== -1;
  }

  toggleImportMatchField(path: string, checked: boolean) {
    if (checked && !this.isImportMatchField(path)) {
      this.view.importMatchFields.push(path);
    } else if (!checked) {
      this.view.importMatchFields = this.view.importMatchFields.filter(matchPath => matchPath !== path);
    }
  }

  // ----- Actions -----

  addAction() {
    this.view.actions.push({ key: '', name: '', route: '' });
  }

  removeAction(index: number) {
    this.view.actions.splice(index, 1);
  }

  // ----- Save / Delete -----

  buildPayload() {
    const payload: any = {};

    // Never unset the identifying fields; leave them as-is when cleared
    if (this.view.name) {
      payload.name = this.view.name;
    }
    if (this.view.key) {
      payload.key = this.view.key;
    }
    if (this.view.collection) {
      payload.collection = this.view.collection;
    }
    payload.active = this.view.active !== false;
    payload.importable = this.view.importable ? true : null;
    payload.creatable = this.view.creatable ? true : null;

    payload.fields = this.view.fields;
    payload.activeFields = this.view.activeFields;
    payload.filters = Object.keys(this.view.filters || {}).length > 0 ? this.view.filters : null;

    const actions = this.view.actions
      .filter(action => action.key)
      .map(action => {
        // Preserve every property the action carries (type, component, class,
        // data, filters, count, aboveSave, ...) — the editor UI only edits
        // key/name/route, but must not destroy the rest on save.
        const cleanAction: any = { ...action };
        if (!cleanAction.name) {
          delete cleanAction.name;
        }
        if (!cleanAction.route) {
          delete cleanAction.route;
        }
        return cleanAction;
      });
    payload.actions = actions.length > 0 ? actions : null;

    payload.importMatchFields = this.view.importMatchFields.length > 0 ? this.view.importMatchFields : null;

    return payload;
  }

  async save() {
    if (!this.view.name) {
      AppComponent.showSnack('View name is required.', 'OK', 5000);
      return;
    }

    if (!this.view.collection) {
      AppComponent.showSnack('View collection is required.', 'OK', 5000);
      return;
    }

    if (this.saving) {
      return;
    }

    this.saving = true;
    LoadingComponent.showLoading();

    try {
      if (this.isNew) {
        const created: any = await this.ioService.post('/viewEditor/createView', {
          view: this.buildPayload()
        });
        AppComponent.showSnack('View created.', 'OK', 3000);
        this.router.navigate(['/editor/views', created._id], { replaceUrl: true });
      } else {
        const updated: any = await this.ioService.post('/viewEditor/updateView', {
          viewId: this.viewId,
          changes: this.buildPayload()
        });
        this.setView(updated);
        AppComponent.showSnack('View saved.', 'OK', 3000);
      }
    } catch (err) {
      AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to save view.', 'OK', 5000);
    }

    this.saving = false;
    LoadingComponent.hideLoading();
  }

  deactivate() {
    const dialogRef = AppComponent.showMessage(
      'Deactivate View',
      `Deactivate the view "${this.view.name}"? It will no longer be available to users.`,
      ['Confirm', 'Cancel'],
      false
    );

    dialogRef.afterClosed().subscribe(async (result: DialogOption) => {
      if (!result || result.value !== 'Confirm') {
        return;
      }

      try {
        await this.ioService.post('/viewEditor/deleteView', {
          viewId: this.viewId
        });
        AppComponent.showSnack('View deactivated.', 'OK', 3000);
        this.router.navigate(['/editor/views']);
      } catch (err) {
        AppComponent.showSnack(err.error && err.error.msg ? err.error.msg : 'Unable to deactivate view.', 'OK', 5000);
      }
    });
  }

  back() {
    this.router.navigate(['/editor/views']);
  }
}
